import React, { useState } from 'react';
import TerminalCard from './TerminalCard';
import { useEffect } from 'react';
import { TERMINALS } from '../lib/terminals';
import emailjs from 'emailjs-com';
import { createCheckoutSession } from '../lib/stripe';
import { useNavigate } from 'react-router-dom';
import { CreditCard, X, Check, AlertCircle as Alert, Info } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/auth/hooks';
import { TerminalRequestForm } from './TerminalRequestForm';
import { FullScreenLoader } from './FullScreenLoader';
import toast from 'react-hot-toast';
import { BottomSheet } from './ui/BottomSheet';
import { NewTerminalWarningModal } from './NewTerminalWarningModal';

const saveAs = (blob: Blob, filename: string) => {
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	setTimeout(() => {
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	}, 0);
};

const SERVICE_ID = 'service_xuvopc8'; // עדכן לפי הצורך
const TEMPLATE_ID = 'template_dl34urm'; // עדכן לפי הצורך
const USER_ID = 'a5OPti5YKYKk2ZKIM'; // עדכן לפי הצורך

// הגדרת מותגים וספקים
const BRANDS = [
	{
		key: 'visa',
		label: 'ויזה',
		providers: [
			'קארדקום',
			'טרנזילה',
			'ויזה כאל',
			'לאומי כארד',
			'ישראכרט',
		],
	},
	{
		key: 'mastercard',
		label: 'מאסטרקארד',
		providers: [
			'קארדקום',
			'טרנזילה',
			'ויזה כאל',
			'לאומי כארד',
			'ישראכרט',
		],
	},
	{
		key: 'isracard',
		label: 'ישראכרט',
		providers: [
			'קארדקום',
			'טרנזילה',
			'ויזה כאל',
			'לאומי כארד',
			'ישראכרט',
		],
	},
	{
		key: 'amex',
		label: 'אמריקן אקספרס',
		providers: ['קארדקום', 'טרנזילה', 'ישראכרט'],
	},
	{
		key: 'diners',
		label: 'דיינרס',
		providers: ['קארדקום', 'טרנזילה', 'ויזה כאל'],
	},
];

// אייקונים לכל מותג
const BRAND_ICONS: Record<string, React.ReactNode> = {
	visa: <img src="https://upload.wikimedia.org/wikipedia/commons/4/41/Visa_Logo.png" alt="Visa" className="h-7 w-7 inline align-middle mr-2 bg-white rounded" loading="lazy" />,
	mastercard: <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-7 w-7 inline align-middle mr-2 bg-white rounded" loading="lazy" />,
	isracard: <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRlmbDAFuWhrO398GPGV8Z6FMXgcrjs1VXatQ&s" alt="Isracard" className="h-7 w-7 inline align-middle mr-2 bg-white rounded" loading="lazy" />,
	amex: <img src="https://he.americanexpress.co.il/globalassets/CardsImages/91_105_-1/hotnewlogo.jpg" alt="Amex" className="h-7 w-7 inline align-middle mr-2 bg-white rounded" loading="lazy" />,
	diners: <img src="https://www.diners-store.co.il/img/assets/logo_diners_new.svg" alt="Diners" style={{ height: '32px', width: 'auto', minWidth: '32px', marginRight: '0.5rem', background: '#fff', borderRadius: '6px', display: 'inline-block', verticalAlign: 'middle' }} loading="lazy" />,
};

/*
הסבר למה צריך מייל לקוח:
Stripe דורש כתובת אימייל עבור כל תשלום/מנוי שנוצר ב-Checkout.
האימייל משמש:
- לשליחת קבלה ללקוח.
- לזיהוי הלקוח במערכת Stripe (לניהול מנויים, תשלומים, ביטולים וכו').
- למעקב אחרי עסקאות ותמיכה.
לכן, חובה להעביר אימייל תקין בכל קריאה ל-createCheckoutSession.
אם אין אימייל, Stripe לא יאפשר ליצור תשלום/מנוי.
*/

interface AddTerminalDialogProps {
	customerEmailFromContext?: string;
	onNewRequest?: (request: any) => void;
	continueRequest?: any;
	onContinueComplete?: () => void;
	hasActiveTerminal?: boolean;
	hasPendingRequest?: boolean;
}

export default function AddTerminalDialog({
	customerEmailFromContext,
	onNewRequest,
	continueRequest,
	onContinueComplete,
	hasActiveTerminal = false,
	hasPendingRequest = false
}: AddTerminalDialogProps) {
	const { user, business } = useAuth();
	const [open, setOpen] = useState(false);
	const [selected, setSelected] = useState<string | null>(null);
	const [showForm, setShowForm] = useState(false);
	const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
	const [form, setForm] = useState({
		businessName: '',
		regType: '',
		regId: '',
		street: '',
		streetNumber: '',
		city: '',
		businessCategory: '',
		contactName: '',
		contactPhone: '',
		contactEmail: '',
		shippingFullAddress: '',
		shippingContactName: '',
		shippingContactPhone: '',
		terminalModel: '',
		terminalsQty: 1,
		accessory: '',
		notes: '',
		brands: [],
		providers: {},
		providerNumbers: {},
		selectedProviders: [],
		cardMapping: {},
		hasTerminal: false,
	});
	const [submitted, setSubmitted] = useState(false);
	const [showMonthlyDialog, setShowMonthlyDialog] = useState(false);
	const [requests, setRequests] = useState<any[]>([]);
	const [isCreatingRequest, setIsCreatingRequest] = useState(false);
	const [loadingMessage, setLoadingMessage] = useState<string>('');
	const [loadingSubMessage, setLoadingSubMessage] = useState<string>('');
	const [showStatusSummary, setShowStatusSummary] = useState(false);
	const [statusSummaryData, setStatusSummaryData] = useState<any>(null);
	const [showWarningModal, setShowWarningModal] = useState(false);

	// שלח event כשהדיאלוג נפתח/נסגר
	React.useEffect(() => {
		const isDialogOpen = open || showMonthlyDialog || showForm;
		window.dispatchEvent(new CustomEvent('ui:terminal-dialog', { detail: { open: isDialogOpen } }));
	}, [open, showMonthlyDialog, showForm]);
	const navigate = useNavigate();

	const handleContinue = async () => {

		// אם אין business מה-context, ננסה לקחת מה-localStorage
		let businessId = business?.id;
		if (!businessId) {
			const storedBusinessId = localStorage.getItem('businessId');

			businessId = storedBusinessId;
		}

		if (selected && user && businessId) {
			// מנע כפילות - אם כבר יוצר בקשה ברגע זה
			if (isCreatingRequest) {

				return;
			}

			setIsCreatingRequest(true);

			const priceIdOneTime = 'price_1Rxqk8Rag1GKV1qDtmetkw0v';
			const priceIdMonthly = 'price_1RxqkjRag1GKV1qDyR8XXlcp';

			// נשתמש באימייל של היוזר אם אין אימייל אחר
			const customerEmail = customerEmailFromContext || form.contactEmail || user.email;

			if (!customerEmail) {
				alert('שגיאה: לא נמצא כתובת אימייל. אנא נסה להתחבר מחדש.');
				setIsCreatingRequest(false);
				return;
			}

			// יצירת בקשה בדאטאבייס
			const terminalObj = Object.values(TERMINALS).find((t) => t.key === selected);
			const { data: request, error: dbError } = await supabase
				.from('terminal_requests')
				.insert({
					user_id: user.id,
					business_id: businessId,
					terminal_model: terminalObj?.name || selected,
					status: 'draft',
					one_time_payment_status: form.hasTerminal ? 'skipped' : 'pending',
					monthly_payment_status: 'pending',
				})
				.select()
				.single();

			if (dbError || !request) {

				alert('שגיאה ביצירת הבקשה. נסה שוב.');
				setIsCreatingRequest(false);
				return;
			}

			setCurrentRequestId(request.id);
			const metadata = {
				businessName: form.businessName,
				request_id: request.id
			};

			try {
				const baseUrl = window.location.origin;
				if (form.hasTerminal) {
					const successUrl = `${baseUrl}/settings/invoices?step=form&request_id=${request.id}&session_id={CHECKOUT_SESSION_ID}`;

					const checkoutUrl = await createCheckoutSession(priceIdMonthly, "subscription", customerEmail, metadata, successUrl);

					if (checkoutUrl) {

						window.location.href = checkoutUrl;
					} else {

						alert('שגיאה ביצירת תשלום. נסה שוב.');
						setIsCreatingRequest(false);
					}
				} else {
					const successUrl = `${baseUrl}/settings/invoices?step=monthly&request_id=${request.id}&session_id={CHECKOUT_SESSION_ID}`;

					const checkoutUrl = await createCheckoutSession(priceIdOneTime, "one-time", customerEmail, metadata, successUrl);

					if (checkoutUrl) {

						window.location.href = checkoutUrl;
					} else {

						alert('שגיאה ביצירת תשלום. נסה שוב.');
						setIsCreatingRequest(false);
					}
				}
			} catch (err) {

				alert(`שגיאה: ${err.message || 'נסה שוב'}`);
				setIsCreatingRequest(false);
			}
		}
	};

	// טיפול בהמשך הזמנה - פשוט וברור
	React.useEffect(() => {
		if (!continueRequest || !user || !business) return;

		const handleContinue = async () => {

			setCurrentRequestId(continueRequest.id);

			// טען form_data אם קיים
			if (continueRequest.form_data) {
				setForm(prev => ({ ...prev, ...continueRequest.form_data }));
			}

			// וודא ש-terminalModel נטען מה-continueRequest
			if (continueRequest.terminal_model) {
				setForm(prev => ({ ...prev, terminalModel: continueRequest.terminal_model }));
			}

			const oneTimeCompleted = continueRequest.one_time_payment_status === 'completed';
			const monthlyCompleted = continueRequest.monthly_payment_status === 'completed';

			// הצג סיכום סטטוס למשתמש
			setStatusSummaryData({
				terminalModel: continueRequest.terminal_model,
				oneTimeCompleted,
				monthlyCompleted,
				status: continueRequest.status
			});
			setShowStatusSummary(true);
		};

		handleContinue();
	}, [continueRequest?.id, continueRequest?._trigger, user, business]);

	// הסתר/הצג בר תחתון בהתאם לסטטוס הפופאפ
	React.useEffect(() => {
		const bottomNav = document.querySelector('nav') as HTMLElement;
		if (showStatusSummary && bottomNav) {
			bottomNav.style.zIndex = '-1';
			bottomNav.style.opacity = '0';
			bottomNav.style.pointerEvents = 'none';
		} else if (bottomNav) {
			bottomNav.style.zIndex = '';
			bottomNav.style.opacity = '';
			bottomNav.style.pointerEvents = '';
		}
		return () => {
			if (bottomNav) {
				bottomNav.style.zIndex = '';
				bottomNav.style.opacity = '';
				bottomNav.style.pointerEvents = '';
			}
		};
	}, [showStatusSummary]);

	// פונקציה להמשך אחרי סיכום הסטטוס
	const handleContinueAfterSummary = async () => {
		setShowStatusSummary(false);

		if (!continueRequest) return;

		const oneTimeCompleted = continueRequest.one_time_payment_status === 'completed' ||
		                         continueRequest.one_time_payment_status === 'skipped';
		const monthlyCompleted = continueRequest.monthly_payment_status === 'completed';
		const hasTerminal = continueRequest.form_data?.hasTerminal || false;
		const status = continueRequest.status;

		// 1. אם הסטטוס הוא form_pending או waiting_for_shipment - פתח טופס
		if (status === 'form_pending' || status === 'waiting_for_shipment') {
			if (oneTimeCompleted && monthlyCompleted) {
				toast.success('מעבר למילוי טופס פרטי המסוף...');
				setTimeout(() => {
					setShowForm(true);
					setOpen(true);
				}, 500);
				return;
			}
		}

		// 2. אם הסטטוס הוא monthly_pending - פתח תשלום חודשי
		if (status === 'monthly_pending') {
			toast.success('מעבר לתשלום חודשי...');
			setTimeout(() => {
				setShowMonthlyDialog(true);
				setOpen(true);
			}, 500);
			return;
		}

		// 3. אם הסטטוס הוא payment_completed - צריך תשלום חודשי
		if (status === 'payment_completed') {
			if (oneTimeCompleted && !monthlyCompleted) {
				toast.success('התשלום החד פעמי הושלם! עובר לתשלום חודשי...');
				setTimeout(() => {
					setShowMonthlyDialog(true);
					setOpen(true);
				}, 500);
				return;
			}
		}

		// 4. אם הסטטוס הוא draft - התחל תשלום חד פעמי
		if (status === 'draft') {
			// אם יש לו כבר מסוף - דלג לתשלום חודשי
			if (hasTerminal) {
				toast.success('מעבר לתשלום חודשי...');
				setTimeout(() => {
					setShowMonthlyDialog(true);
					setOpen(true);
				}, 500);
				return;
			}

			// אחרת - צריך תשלום חד פעמי
			setLoadingMessage('מעבר לתשלום חד פעמי...');
			setLoadingSubMessage('רגע אחד, אנחנו מכינים עבורך את עמוד התשלום');

			const priceIdOneTime = 'price_1Rxqk8Rag1GKV1qDtmetkw0v';
			const customerEmail = customerEmailFromContext || continueRequest.form_data?.contactEmail || user?.email;

			if (!customerEmail) {
				toast.error('שגיאה: לא נמצא כתובת אימייל');
				setLoadingMessage('');
				return;
			}

			const metadata = {
				businessName: continueRequest.form_data?.businessName || form.businessName,
				request_id: continueRequest.id
			};
			const baseUrl = window.location.origin;
			const successUrl = `${baseUrl}/settings/invoices?step=monthly&request_id=${continueRequest.id}&session_id={CHECKOUT_SESSION_ID}`;

			try {
				const checkoutUrl = await createCheckoutSession(priceIdOneTime, "one-time", customerEmail, metadata, successUrl);
				if (checkoutUrl) {
					window.location.href = checkoutUrl;
				} else {
					toast.error('שגיאה ביצירת תשלום');
					setLoadingMessage('');
				}
			} catch (err) {

				toast.error('שגיאה ביצירת תשלום');
				setLoadingMessage('');
			}
			return;
		}

		// 5. אם לא התאים לאף תנאי - הצג הודעת שגיאה

		toast.error('שגיאה: לא ניתן להמשיך בתהליך. אנא פנה לתמיכה.');
	};

	// בדוק אם צריך להציג דיאלוג לתשלום חודשי אחרי חזרה מהתשלום החד פעמי
	React.useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const step = params.get('step');
		const sessionId = params.get('session_id');
		const requestId = params.get('request_id');

		if (step === 'monthly' && requestId) {
			setCurrentRequestId(requestId);
			// אם יש session_id, עדכן את הבקשה
			if (sessionId) {
				supabase
					.from('terminal_requests')
					.update({
						one_time_payment_status: 'completed',
						stripe_session_id_onetime: sessionId,
						status: 'monthly_pending'
					})
					.eq('id', requestId)
					.then(() => {
						setShowMonthlyDialog(true);
						setOpen(true);
					});
			} else {
				// פשוט פתח את הדיאלוג
				setShowMonthlyDialog(true);
				setOpen(true);
			}
		} else if (step === 'form' && requestId) {
			setCurrentRequestId(requestId);
			// אם יש session_id, עדכן את הבקשה
			if (sessionId) {
				supabase
					.from('terminal_requests')
					.update({
						monthly_payment_status: 'completed',
						stripe_session_id_monthly: sessionId,
						status: 'form_pending'
					})
					.eq('id', requestId)
					.then(() => {
						// טען את הטיוטה מה-DB אם קיימת
						supabase
							.from('terminal_requests')
							.select('form_data')
							.eq('id', requestId)
							.maybeSingle()
							.then(({ data }) => {
								if (data?.form_data) {

									setForm(prev => ({ ...prev, ...data.form_data }));
								}
								setShowForm(true);
								setOpen(true);
							});
					});
			} else {
				// טען את הטיוטה מה-DB אם קיימת
				supabase
					.from('terminal_requests')
					.select('form_data')
					.eq('id', requestId)
					.maybeSingle()
					.then(({ data }) => {
						if (data?.form_data) {

							setForm(prev => ({ ...prev, ...data.form_data }));
						}
						setShowForm(true);
						setOpen(true);
					});
			}
		}
	}, []);

	const handleMonthlyPayment = async () => {

		const priceIdMonthly = 'price_1RxqkjRag1GKV1qDyR8XXlcp';
		const customerEmail = customerEmailFromContext || form.contactEmail || user?.email;

		if (!customerEmail) {
			alert('שגיאה: לא נמצא כתובת אימייל. אנא נסה להתחבר מחדש.');
			return;
		}

		const metadata = {
			businessName: form.businessName,
			request_id: currentRequestId
		};
		try {
			const baseUrl = window.location.origin;
			const successUrl = `${baseUrl}/settings/invoices?step=form&request_id=${currentRequestId}&session_id={CHECKOUT_SESSION_ID}`;
			const checkoutUrl = await createCheckoutSession(priceIdMonthly, "subscription", customerEmail, metadata, successUrl);
			if (checkoutUrl) {
				window.location.href = checkoutUrl;
			}
		} catch (err) {

		}
	};

	const handleChange = (
		e: React.ChangeEvent<
			HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
		>
	) => {
		const { name, value, type, checked } = e.target;

		// New logic for provider companies selection
		if (name === 'selectedProviders') {
			const provider = value;
			setForm((prev) => {
				const currentProviders = prev.selectedProviders || [];
				const newProviders = currentProviders.includes(provider)
					? currentProviders.filter((p: string) => p !== provider)
					: [...currentProviders, provider];
				return { ...prev, selectedProviders: newProviders };
			});
		} else if (name.startsWith('providerNumber-')) {
			const provider = name.replace('providerNumber-', '');
			setForm((prev) => {
				const newProviderNumbers = { ...(prev.providerNumbers || {}), [provider]: value };
				return {
					...prev,
					providerNumbers: newProviderNumbers,
				};
			});
		} else if (name === 'cardMapping') {
			setForm((prev) => ({
				...prev,
				cardMapping: value,
			}));
		} else if (name === 'brands') {
			const brand = value;
			setForm((prev) => {
				const brands = prev.brands.includes(brand)
					? prev.brands.filter((b: string) => b !== brand)
					: [...prev.brands, brand];
				return { ...prev, brands };
			});
		} else if (name.startsWith('provider-')) {
			const brand = name.replace('provider-', '');
			setForm((prev) => {
				const newProviders = { ...prev.providers, [brand]: value };
				let newProviderNumbers = { ...prev.providerNumbers };
				// If provider changed, clear providerNumber for this brand
				newProviderNumbers[brand] = '';
				// If another brand already has this provider, copy its providerNumber
				const firstBrand = Object.entries(newProviders).find(
					([b, p]) => p === value && b !== brand && prev.providerNumbers[b]
				);
				if (firstBrand) {
					newProviderNumbers[brand] = prev.providerNumbers[firstBrand[0]];
				}
				return {
					...prev,
					providers: newProviders,
					providerNumbers: newProviderNumbers,
				};
			});
		} else {
			setForm((prev) => ({
				...prev,
				[name]: type === 'checkbox' ? checked : value,
			}));
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		// יצירת תוכן CSV
		const flattenBrands = form.brands.map((brand: string) => {
			return {
				brand,
				provider: form.providers[brand] || '',
				providerNumber: form.providerNumbers[brand] || '',
			};
		});
		const rows = [
			[
				'שם בית עסק',
				'צורת ההתאגדות',
				'ח"פ / ע.מ',
				'רחוב',
				'מספר',
				'עיר',
				'תחום/אופי פעילות',
				'שם איש קשר',
				'טלפון נייד',
				'כתובת מלאה למשלוח',
				'איש קשר למשלוח',
				'טלפון נייד למשלוח',
				'דגם מסוף',
				'כמות מסופים',
				'תוספת אופציונלית',
				'הערות',
				'מותג',
				'ספק',
				'מספר ספק',
			],
			...flattenBrands.map(b => [
				form.businessName,
				form.regType,
				form.regId,
				form.street,
				form.streetNumber,
				form.city,
				form.businessCategory,
				form.contactName,
				form.contactPhone,
				form.shippingFullAddress,
				form.shippingContactName,
				form.shippingContactPhone,
				form.terminalModel,
				form.terminalsQty,
				form.accessory,
				form.notes,
				b.brand,
				b.provider,
				b.providerNumber,
			]),
		];
		const csv = rows.map(r => r.map(x => `"${x}"`).join(',')).join('\n');

		// יצירת מחרוזת Base64 תקינה של ה-CSV
		const csvBase64 = btoa(unescape(encodeURIComponent(csv)));
		const dataUri = `data:text/csv;base64,${csvBase64}`;

		// שליחת המייל עם מצורף (שדה attachments הוא מחרוזת אחת!)
		try {
			await emailjs.send(
				SERVICE_ID,
				TEMPLATE_ID,
				{
					message: csv,
					from_name: form.businessName,
					attachments: dataUri,
				},
				USER_ID
			);
		} catch (err) {

		}

		// שליחת webhook ל-n8n
		try {
			const webhookUrl = 'https://ofekperetz.app.n8n.cloud/webhook/06ebebce-00db-45de-bd0a-2357563517d5';
			const webhookPayload = {
				businessName: form.businessName,
				regType: form.regType,
				regId: form.regId,
				street: form.street,
				streetNumber: form.streetNumber,
				city: form.city,
				businessCategory: form.businessCategory,
				contactName: form.contactName,
				contactPhone: form.contactPhone,
				contactEmail: form.contactEmail,
				shippingFullAddress: form.shippingFullAddress,
				shippingContactName: form.shippingContactName,
				shippingContactPhone: form.shippingContactPhone,
				terminalModel: form.terminalModel,
				terminalsQty: form.terminalsQty,
				accessory: form.accessory || '',
				notes: form.notes || '',
				brands: flattenBrands,
				providers: form.providers,
				providerNumbers: form.providerNumbers || {},
				selectedProviders: form.selectedProviders || [],
				cardMapping: form.cardMapping || {},
				submittedAt: new Date().toISOString(),
			};

			await fetch(webhookUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(webhookPayload),
			});
		} catch (err) {

		}

		// עדכן את הבקשה בדאטאבייס
		if (currentRequestId) {
			await supabase
				.from('terminal_requests')
				.update({
					form_submitted: true,
					form_data: form,
					status: 'waiting_for_shipment'
				})
				.eq('id', currentRequestId);
		}

		setSubmitted(true);
		// נקה את ה-URL params
		window.history.replaceState({}, '', window.location.pathname);
	};

	

	const handleClose = (skipWarning = false) => {
		if (showForm && !submitted && !skipWarning) {
			alert('כבר ביצעת תשלום. על מנת שנוכל להמשיך לטפל בבקשה שלך, אנא מלא ושלח את הטופס.');
			return;
		}
		setOpen(false);
		setShowForm(false);
		setShowMonthlyDialog(false);
		setShowStatusSummary(false);
		setStatusSummaryData(null);
		setSelected(null);
		setCurrentRequestId(null);
		if (onContinueComplete) {
			onContinueComplete();
		}
		setForm({
			businessName: '',
			regType: '',
			regId: '',
			street: '',
			streetNumber: '',
			city: '',
			businessCategory: '',
			contactName: '',
			contactPhone: '',
			contactEmail: '',
			shippingFullAddress: '',
			shippingContactName: '',
			shippingContactPhone: '',
			terminalModel: '',
			terminalsQty: 1,
			accessory: '',
			notes: '',
			brands: [],
			providers: {},
			providerNumbers: {},
			selectedProviders: [],
			cardMapping: {},
			hasTerminal: false,
		});
		setSubmitted(false);
	};

	// עדכן את דגם המסוף אוטומטית כאשר נבחר מסוף
	React.useEffect(() => {
		if (selected) {
			const terminalObj = Object.values(TERMINALS).find(
				(t) => t.key === selected
			);
			setForm((prev) => ({
				...prev,
				terminalModel: terminalObj?.name || '',
			}));
			// שמור את דגם המסוף ב-localStorage
			if (terminalObj?.name) {
				localStorage.setItem('selectedTerminalModel', terminalObj.name);
			}
		}
	}, [selected]);

	// הוספת פונקציה ליצוא נתוני הטופס ל-Excel (CSV)
	function exportFormToExcel(form: any) {
		const flattenBrands = form.brands.map((brand: string) => {
			return {
				brand,
				provider: form.providers[brand] || '',
				providerNumber: form.providerNumbers[brand] || '',
			};
		});
		const rows = [
			[
				'שם בית עסק',
				'צורת ההתאגדות',
				'ח"פ / ע.מ',
				'רחוב',
				'מספר',
				'עיר',
				'תחום/אופי פעילות',
				'שם איש קשר',
				'טלפון נייד',
				'כתובת מלאה למשלוח',
				'איש קשר למשלוח',
				'טלפון נייד למשלוח',
				'דגם מסוף',
				'כמות מסופים',
				'תוספת אופציונלית',
				'הערות',
				'מותג',
				'ספק',
				'מספר ספק',
			],
			// לכל מותג שורה נפרדת
			...flattenBrands.map(b => [
				form.businessName,
				form.regType,
				form.regId,
				form.street,
				form.streetNumber,
				form.city,
				form.businessCategory,
				form.contactName,
				form.contactPhone,
				form.shippingFullAddress,
				form.shippingContactName,
				form.shippingContactPhone,
				form.terminalModel,
				form.terminalsQty,
				form.accessory,
				form.notes,
				b.brand,
				b.provider,
				b.providerNumber,
			]),
		];

		const csv = rows.map(r => r.map(x => `"${x}"`).join(',')).join('\n');
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		saveAs(blob, 'order.csv');
	}

	// בקשות שמורות (טיוטות/היסטוריה) ב-localStorage
	function getTerminalRequests() {
  const raw = localStorage.getItem('terminalRequests');
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(-1) : [];
  } catch {
    return [];
  }
}

// שמור בקשה חדשה/טיוטה
function saveTerminalRequest(request: any) {
  localStorage.setItem('terminalRequests', JSON.stringify([request]));
}

	// טען בקשות מה-localStorage כאשר הקומפוננטה נטענת
	useEffect(() => {
		setRequests(getTerminalRequests());
	}, []);

	// שמור טיוטה בכל שינוי בטופס (אם לא נשלח)
	useEffect(() => {
		if (!submitted && showForm && currentRequestId) {
			// שמור ב-localStorage
			const draft = { ...form, id: form.id || Date.now(), status: submitted ? 'sent' : 'draft' };
			saveTerminalRequest(draft);
			setRequests(getTerminalRequests());

			// שמור גם ב-Supabase עם debounce
			const timer = setTimeout(() => {
				supabase
					.from('terminal_requests')
					.update({
						form_data: form,
						updated_at: new Date().toISOString()
					})
					.eq('id', currentRequestId)
					.then(() => console.log('Draft saved to Supabase'));
			}, 2000); // שמור אחרי 2 שניות של אי-פעילות

			return () => clearTimeout(timer);
		}
	}, [form, submitted, showForm, currentRequestId]);

	// אחרי שליחה, עדכן סטטוס הבקשה ל-"sent"
	useEffect(() => {
		if (submitted && showForm) {
			const sentReq = { ...form, id: form.id || Date.now(), status: 'sent' };
			saveTerminalRequest(sentReq);
			setRequests(getTerminalRequests());
		}
	}, [submitted, showForm]);

	// עריכת טיוטה קיימת
	function handleEditRequest(request: any) {
		setForm({ ...request }); // שמור את כל השדות בדיוק כפי שמולאו
		setShowForm(true);
		setOpen(true);
		setSelected(Object.values(TERMINALS).find(t => t.name === request.terminalModel)?.key || null);
		setSubmitted(false);
	}

	const handleOpenTerminalRequest = () => {
		if (hasActiveTerminal) {
			setShowWarningModal(true);
		} else {
			setCurrentRequestId(null);
			setIsCreatingRequest(false);
			setOpen(true);
		}
	};

	const handleContinueFromWarning = () => {
		setShowWarningModal(false);
		setCurrentRequestId(null);
		setIsCreatingRequest(false);
		setOpen(true);
	};

	return (
		<>
			<NewTerminalWarningModal
				isOpen={showWarningModal}
				onClose={() => setShowWarningModal(false)}
				onContinue={handleContinueFromWarning}
			/>

			<div className="relative group">
				<button
					className={`w-full py-3.5 rounded-xl font-semibold text-[16px] transition-all flex items-center justify-center gap-2 ${
						hasPendingRequest
							? 'bg-gray-300 text-gray-500 cursor-not-allowed'
							: 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
					}`}
					onClick={handleOpenTerminalRequest}
					disabled={hasPendingRequest}
				>
					<CreditCard className="h-5 w-5" />
					הזמן מסוף חדש
				</button>
				{hasPendingRequest && (
					<div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-gray-900 text-white text-[12px] px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 text-center">
						לא ניתן להוסיף מסוף נוסף כאשר יש הזמנה בתהליך
						<div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
					</div>
				)}
			</div>
			<BottomSheet
				isOpen={open && !showStatusSummary}
				onClose={handleClose}
				ariaLabel={showForm ? 'טופס חיבור מסוף' : 'בחירת מסוף סליקה'}
			>
				{!showForm && (
					<div className="bg-gradient-to-b from-blue-50 to-white border-b border-gray-100 px-4 py-3 relative shrink-0 rounded-t-3xl">
						<button
							className="absolute top-3 left-4 w-8 h-8 rounded-full bg-white hover:bg-gray-50 shadow-sm flex items-center justify-center text-gray-600 transition-all active:scale-95 z-10"
							onClick={handleClose}
							type="button"
						>
							<X className="h-5 w-5" />
						</button>
						<h2 className="text-[18px] font-bold text-gray-900 text-center">
							בחירת מסוף סליקה
						</h2>
						<p className="text-[13px] text-gray-500 text-center mt-1">בחר את המסוף המתאים לעסק שלך</p>
					</div>
				)}

				{!showForm && <div className="p-4 pb-safe">
				{!showForm && !submitted && (
					<>
						{/* Process explanation with enhanced design */}
						<div className="bg-gradient-to-br from-blue-50 via-blue-100 to-indigo-100 rounded-2xl p-5 mb-5 border border-blue-200 shadow-sm">
							<h3 className="text-[16px] font-bold text-blue-900 mb-4 flex items-center gap-2">
								<div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
									<Info className="h-4 w-4 text-white" />
								</div>
								איך זה עובד?
							</h3>
							<div className="space-y-3">
								<div className="flex items-start gap-3 p-3 bg-white/60 rounded-xl backdrop-blur-sm">
									<div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-md">1</div>
									<div className="text-[13px] text-blue-900 flex-1">
										<strong className="block mb-1">בחר מסוף</strong>
										<span className="text-blue-700">בחר את דגם המסוף המתאים לעסק שלך</span>
									</div>
								</div>
								<div className="flex items-start gap-3 p-3 bg-white/60 rounded-xl backdrop-blur-sm">
									<div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-md">2</div>
									<div className="text-[13px] text-blue-900 flex-1">
										<strong className="block mb-1">תשלום</strong>
										<span className="text-blue-700">תשלום חד פעמי למסוף + מנוי חודשי לשירותים</span>
									</div>
								</div>
								<div className="flex items-start gap-3 p-3 bg-white/60 rounded-xl backdrop-blur-sm">
									<div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-md">3</div>
									<div className="text-[13px] text-blue-900 flex-1">
										<strong className="block mb-1">מילוי טופס</strong>
										<span className="text-blue-700">מלא פרטים על העסק ופרטי סליקה</span>
									</div>
								</div>
								<div className="flex items-start gap-3 p-3 bg-white/60 rounded-xl backdrop-blur-sm">
									<div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-md">4</div>
									<div className="text-[13px] text-blue-900 flex-1">
										<strong className="block mb-1">קבלת מסוף</strong>
										<span className="text-blue-700">המסוף יישלח תוך 3-10 ימי עסקים</span>
									</div>
								</div>
								<div className="flex items-start gap-3 p-3 bg-white/60 rounded-xl backdrop-blur-sm">
									<div className="w-7 h-7 rounded-full bg-gradient-to-br from-green-600 to-green-700 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-md">5</div>
									<div className="text-[13px] text-blue-900 flex-1">
										<strong className="block mb-1">הפעלה</strong>
										<span className="text-blue-700">חבר את המסוף למערכת עם הפרטים שתקבל</span>
									</div>
								</div>
							</div>
						</div>

						{/* Comparison table */}
						{Object.values(TERMINALS).length > 1 && (
							<div className="bg-amber-50 rounded-2xl p-4 mb-4 border border-amber-200">
								<h4 className="text-[14px] font-bold text-amber-900 mb-3">השוואה מהירה</h4>
								<div className="space-y-2 text-[13px]">
									<div className="flex justify-between items-center">
										<span className="text-amber-900 font-medium">N6 (ניידי)</span>
										<span className="text-amber-800">מסך מגע | נטען | WiFi</span>
									</div>
									<div className="flex justify-between items-center">
										<span className="text-amber-900 font-medium">A35 (קבוע)</span>
										<span className="text-amber-800">מסך גדול | שולחני | חיבור קווי</span>
									</div>
								</div>
							</div>
						)}

						<div className="space-y-3 mb-4">
							{Object.values(TERMINALS).map((terminal) => (
								<div
									key={terminal.key}
									onClick={() => setSelected(terminal.key)}
									className={`
										relative bg-white rounded-2xl p-5 cursor-pointer transition-all transform active:scale-[0.98]
										${selected === terminal.key
											? 'border-2 border-blue-500 shadow-lg shadow-blue-100 ring-2 ring-blue-200 ring-offset-2'
											: 'border-2 border-gray-200 hover:border-gray-300 hover:shadow-md'
										}
									`}
								>
									{selected === terminal.key && (
										<div className="absolute -top-2 right-4 bg-blue-600 text-white text-[11px] font-bold px-3 py-1 rounded-full shadow-md">
											נבחר
										</div>
									)}
									<div className="flex items-center gap-4">
										<div className="flex-shrink-0 w-24 h-24 flex items-center justify-center bg-white rounded-xl p-2 shadow-sm">
											{terminal.key === 'N6' && (
												<img
													src="https://res.cloudinary.com/drujiiwnt/images/f_auto,q_auto/v1763909441/Wordpress%20Pelecard%20Website/%D7%AA%D7%9E%D7%95%D7%A0%D7%94-%D7%94%D7%99%D7%A8%D7%95-%D7%9E%D7%A1%D7%95%D7%A4%D7%95%D7%9F-N6_566262a6b/%D7%AA%D7%9E%D7%95%D7%A0%D7%94-%D7%94%D7%99%D7%A8%D7%95-%D7%9E%D7%A1%D7%95%D7%A4%D7%95%D7%9F-N6_566262a6b.webp?_i=AA"
													alt="N6 Terminal"
													className="w-full h-full object-contain"
													loading="lazy"
												/>
											)}
											{terminal.key === 'CTP20-P' && (
												<img
													src="https://custom-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_limit,fl_lossy,h_1440,w_720,f_auto,q_auto/1402119/553221_440863.png"
													alt="A35 Terminal"
													className="w-full h-full object-contain"
													loading="lazy"
												/>
											)}
										</div>

										<div className="flex-1">
											<h3 className="text-[18px] font-bold text-gray-900 mb-1">{terminal.name}</h3>
											<p className="text-[13px] text-gray-500 mb-3">
												{terminal.key === 'N6' ? 'מסוף ניידי עם WiFi' : 'מסוף שולחני קבוע'}
											</p>
											<div className="flex items-baseline gap-2">
												<div className="text-[28px] font-bold text-gray-900">$445</div>
												<div className="text-[13px] text-gray-500">תשלום חד פעמי</div>
											</div>
										</div>

										{selected === terminal.key && (
											<div className="flex-shrink-0 w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center shadow-md">
												<Check className="h-4 w-4 text-white" strokeWidth={3} />
											</div>
										)}
									</div>
								</div>
							))}
						</div>
						{selected && (
							<>
								<label className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl cursor-pointer mb-3">
									<input
										type="checkbox"
										checked={form.hasTerminal || false}
										onChange={e =>
											setForm(prev => ({
												...prev,
												hasTerminal: e.target.checked,
											}))
										}
										className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
									/>
									<div className="flex-1">
										<div className="text-[15px] font-medium text-gray-900">יש לי כבר מסוף זה</div>
										<div className="text-[13px] text-gray-500 mt-0.5">רק תשלום חודשי</div>
									</div>
								</label>

								<div className="bg-gray-50 rounded-2xl p-4">
									<div className="text-[15px] font-medium text-gray-900 mb-3">תשלום חודשי</div>
									<div className="space-y-2">
										<div className="flex items-center justify-between">
											<span className="text-[14px] text-gray-600">מערכת ניהול + סליקה</span>
											<span className="text-[14px] font-medium text-gray-900">$20</span>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-[14px] text-gray-600">הפקת חשבוניות</span>
											<span className="text-[14px] font-medium text-gray-900">$8</span>
										</div>
										<div className="border-t border-gray-200 my-2"></div>
										<div className="flex items-center justify-between">
											<span className="text-[15px] font-semibold text-gray-900">סה"כ</span>
											<span className="text-[20px] font-bold text-blue-600">$28</span>
										</div>
									</div>
								</div>
							</>
						)}
					</>
				)}
				</div>}

				{!showForm && !submitted && (
					<div className="bg-white border-t border-gray-100 p-4 flex gap-3">
						<button
							onClick={handleClose}
							className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors active:scale-[0.98] text-[16px]"
						>
							ביטול
						</button>
						<button
							onClick={handleContinue}
							disabled={!selected}
							className={`flex-1 py-3 rounded-xl font-semibold transition-all active:scale-[0.98] text-[16px] ${
								selected
									? 'bg-blue-600 text-white hover:bg-blue-700'
									: 'bg-gray-200 text-gray-400 cursor-not-allowed'
							}`}
						>
							המשך
						</button>
					</div>
				)}
					{showForm && !submitted && (
						<TerminalRequestForm
							form={form}
							handleChange={handleChange}
							handleSubmit={handleSubmit}
							onSaveAndExit={async () => {
								if (currentRequestId) {
									await supabase
										.from('terminal_requests')
										.update({ form_data: form, updated_at: new Date().toISOString() })
										.eq('id', currentRequestId);
									alert('הטיוטה נשמרה בהצלחה. תוכל להמשיך למלא את הטופס בכל עת.');
								}
								handleClose(true);
							}}
							terminalModel={form.terminalModel}
							BRANDS={BRANDS}
							BRAND_ICONS={BRAND_ICONS}
						/>
					)}
					{/* OLD FORM BACKUP START - להסיר אחרי בדיקה */}
					{false && showForm && !submitted && (
						<>
							<div className="bg-blue-50 px-4 py-3 border-b border-blue-100 shrink-0">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
										<span className="text-[14px] font-medium text-blue-700">נשמר אוטומטית</span>
									</div>
									<span className="text-[13px] text-blue-600">{form.terminalModel}</span>
								</div>
							</div>

							<form id="terminal-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-5">
								{/* פרטי עסק */}
								<div className="bg-gray-50 rounded-2xl p-4 space-y-4">
									<h3 className="text-[16px] font-bold text-gray-900 mb-3">פרטי העסק</h3>

									<div>
								<label className="block text-sm font-semibold mb-2 text-gray-900">שם בית עסק</label>
								<input
									type="text"
									name="businessName"
									value={form.businessName}
									onChange={handleChange}
									className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
									required
									placeholder="לדוג' מסעדת גן עדן"
								/>
							</div>
							<div>
								<label className="block text-sm font-semibold mb-2 text-gray-900">צורת ההתאגדות</label>
								<select
									name="regType"
									value={form.regType}
									onChange={handleChange}
									className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900"
									required
								>
									<option value="">בחר...</option>
									<option value="חברה בע״מ">חברה בע״מ</option>
									<option value="עוסק מורשה">עוסק מורשה</option>
									<option value="עמותה">עמותה</option>
									<option value="אחר">אחר</option>
								</select>
							</div>
							<div>
								<label className="block text-sm font-semibold mb-2 text-gray-900">
									ח"פ / ע.מ
								</label>
								<input
									type="text"
									name="regId"
									value={form.regId}
									onChange={handleChange}
									className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
									required
								/>
							</div>
							<div className="flex gap-2">
								<div className="flex-1">
									<label className="block text-sm font-semibold mb-2 text-gray-900">
										רחוב
									</label>
									<input
										type="text"
										name="street"
										value={form.street}
										onChange={handleChange}
										className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
										required
									/>
								</div>
								<div className="w-24">
									<label className="block text-sm font-semibold mb-2 text-gray-900">
										מספר
									</label>
									<input
										type="text"
										name="streetNumber"
										value={form.streetNumber}
										onChange={handleChange}
										className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
										required
									/>
								</div>
							</div>
							<div>
								<label className="block text-sm font-semibold mb-2 text-gray-900">
									עיר
								</label>
								<input
									type="text"
									name="city"
									value={form.city}
									onChange={handleChange}
									className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
									required
								/>
							</div>
							<div>
								<label className="block text-sm font-semibold mb-2 text-gray-900">
									תחום/אופי פעילות
								</label>
								<input
									type="text"
									name="businessCategory"
									value={form.businessCategory}
									onChange={handleChange}
									className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
									required
									placeholder="מסעדה / קמעונאות / שירותים"
								/>
							</div>
								</div>

								{/* פרטי קשר */}
								<div className="bg-gray-50 rounded-2xl p-4 space-y-4">
									<h3 className="text-[16px] font-bold text-gray-900 mb-3">פרטי איש קשר</h3>

									<div>
										<label className="block text-sm font-semibold mb-2 text-gray-900">שם מלא</label>
										<input
											type="text"
											name="contactName"
											value={form.contactName}
											onChange={handleChange}
											className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
											required
											placeholder="ישראל ישראלי"
										/>
									</div>

									<div>
										<label className="block text-sm font-semibold mb-2 text-gray-900">טלפון נייד</label>
										<input
											type="tel"
											name="contactPhone"
											value={form.contactPhone}
											onChange={handleChange}
											className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
											required
											placeholder="050-1234567"
										/>
									</div>

									<div>
										<label className="block text-sm font-semibold mb-2 text-gray-900">כתובת אימייל</label>
										<input
											type="email"
											name="contactEmail"
											value={form.contactEmail}
											onChange={handleChange}
											className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
											required
											placeholder="example@email.com"
										/>
									</div>
								</div>

								{/* פרטי משלוח */}
								<div className="bg-gray-50 rounded-2xl p-4 space-y-4">
									<h3 className="text-[16px] font-bold text-gray-900 mb-3">פרטי משלוח</h3>

									<div>
										<label className="block text-sm font-semibold mb-2 text-gray-900">כתובת מלאה</label>
										<input
											type="text"
											name="shippingFullAddress"
											value={form.shippingFullAddress}
											onChange={handleChange}
											className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
											required
											placeholder="רחוב 123, תל אביב"
										/>
									</div>
									<div>
										<label className="block text-sm font-semibold mb-2 text-gray-900">איש קשר</label>
										<input
											type="text"
											name="shippingContactName"
											value={form.shippingContactName}
											onChange={handleChange}
											className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
											required
											placeholder="ישראל ישראלי"
										/>
									</div>

									<div>
										<label className="block text-sm font-semibold mb-2 text-gray-900">טלפון למשלוח</label>
										<input
											type="tel"
											name="shippingContactPhone"
											value={form.shippingContactPhone}
											onChange={handleChange}
											className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
											required
											placeholder="050-1234567"
										/>
									</div>
								</div>

								{/* פרטי המסוף */}
								<div className="bg-gray-50 rounded-2xl p-4 space-y-4">
									<h3 className="text-[16px] font-bold text-gray-900 mb-3">פרטי המסוף</h3>

									<div>
										<label className="block text-sm font-semibold mb-2 text-gray-700">דגם מסוף</label>
										<input
											type="text"
											name="terminalModel"
											value={form.terminalModel}
											readOnly
											className="w-full border-2 border-gray-200 rounded-xl p-3.5 bg-gray-100 text-gray-600"
										/>
									</div>

									<div>
										<label className="block text-sm font-semibold mb-2 text-gray-900">כמות מסופים</label>
										<input
											type="number"
											name="terminalsQty"
											value={form.terminalsQty}
											min={1}
											onChange={handleChange}
											className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
											required
										/>
									</div>

									<div>
										<label className="block text-sm font-semibold mb-2 text-gray-900">תוספת אופציונלית</label>
										<select
											name="accessory"
											value={form.accessory}
											onChange={handleChange}
											className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900"
										>
											<option value="">ללא</option>
											{selected && (
												<option value={TERMINALS[selected]?.prices.accessoryLabel}>
													{TERMINALS[selected]?.prices.accessoryLabel}
												</option>
											)}
										</select>
									</div>

									<div>
										<label className="block text-sm font-semibold mb-2 text-gray-900">הערות</label>
										<textarea
											name="notes"
											value={form.notes}
											onChange={handleChange}
											className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400 resize-none"
											rows={3}
											placeholder="הערות נוספות..."
										/>
									</div>
								</div>

								{/* מותגים לסליקה */}
								<div className="bg-gray-50 rounded-2xl p-4 space-y-4">
									<h3 className="text-[16px] font-bold text-gray-900 mb-3">מותגים לסליקה</h3>
									<div className="flex flex-wrap gap-2">
										{BRANDS.map((b) => (
											<label key={b.key} className={`inline-flex items-center gap-2 border-2 rounded-xl px-3.5 py-2.5 cursor-pointer transition-all ${
												form.brands.includes(b.key)
													? 'border-blue-500 bg-blue-50'
													: 'border-gray-200 bg-white hover:border-gray-300'
											}`}>
												<input
													type="checkbox"
													name="brands"
													value={b.key}
													checked={form.brands.includes(b.key)}
													onChange={handleChange}
													className="accent-blue-600 w-4 h-4"
												/>
												<span className="flex items-center gap-1.5">
													{BRAND_ICONS[b.key]}
													<span className="font-semibold text-[14px] text-gray-900">{b.label}</span>
												</span>
											</label>
									))}
								</div>
							</div>
							{form.brands.map((brand: string) => {
	const provider = form.providers[brand] || '';
	const firstBrandForProvider = getFirstBrandForProviders(form.brands, form.providers)[provider];
	const isFirst = brand === firstBrandForProvider;
	return (
		<div key={brand} className="mt-2">
			<label className="block text-sm font-semibold mb-2 text-gray-900">
				ספק סליקה עבור {BRAND_ICONS[brand]} {BRANDS.find(b => b.key === brand)?.label}
			</label>
			<select
				name={`provider-${brand}`}
				value={form.providers[brand] || ''}
				onChange={handleChange}
				className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
				required
			>
				<option value="">בחר ספק...</option>
				{BRANDS.find(b => b.key === brand)?.providers.map((p) => (
					<option key={p} value={p}>{p}</option>
				))}
			</select>
			<label className="block font-medium mb-1 text-gray-700 mt-2">
				מספר ספק עבור {BRANDS.find(b => b.key === brand)?.label}
			</label>
			<input
				type="text"
				name={`providerNumber-${brand}`}
				value={form.providerNumbers[brand] || ''}
				onChange={handleChange}
				className="w-full border-2 border-gray-200 rounded-xl p-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white text-gray-900 placeholder-gray-400"
				required
				disabled={!isFirst && !!provider}
			/>
		</div>
	);
})}
							</form>

							{/* כפתורים sticky בתחתית */}
							<div className="bg-white border-t border-gray-200 p-3 shrink-0">
								<div className="space-y-2">
									<button
										type="submit"
										form="terminal-form"
										className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold text-[15px] active:scale-[0.98] transition-transform"
									>
										שלח בקשה
									</button>
									<button
										type="button"
										onClick={() => {
											if (currentRequestId) {
												supabase
													.from('terminal_requests')
													.update({ form_data: form, updated_at: new Date().toISOString() })
													.eq('id', currentRequestId)
													.then(() => {
														alert('הטיוטה נשמרה בהצלחה. תוכל להמשיך למלא את הטופס בכל עת.');
														handleClose();
													});
											} else {
												handleClose();
											}
										}}
										className="w-full py-2.5 bg-white text-gray-700 rounded-xl font-medium text-[14px] border border-gray-300 active:scale-[0.98] transition-transform"
									>
										שמור והמשך אחר כך
									</button>
								</div>
							</div>
						</>
					)}
					{submitted && (
						<div className="p-6 text-center space-y-4 pb-24">
							<div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto shadow-lg">
								<Check className="h-10 w-10 text-white" strokeWidth={3} />
							</div>
							<div>
								<div className="text-[24px] font-bold text-gray-900 mb-2">
									הבקשה נשלחה בהצלחה!
								</div>
								<div className="text-[15px] text-gray-600 mb-4">
									המסוף {form.terminalModel} יישלח אליך תוך 3-10 ימי עסקים
								</div>

								<div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4 text-right space-y-3 border border-blue-200">
									<div className="flex items-center gap-2 text-blue-900 font-bold text-[15px]">
										<Info className="h-5 w-5" />
										<span>מה קורה עכשיו?</span>
									</div>

									<div className="space-y-2.5 text-right">
										<div className="flex items-start gap-2.5">
											<div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</div>
											<div className="text-[13px] text-blue-900">
												<strong>עיבוד ההזמנה</strong><br/>
												<span className="text-[12px] text-blue-700">אנחנו מעבדים את הבקשה שלך ומכינים את המסוף למשלוח</span>
											</div>
										</div>

										<div className="flex items-start gap-2.5">
											<div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</div>
											<div className="text-[13px] text-blue-900">
												<strong>משלוח המסוף</strong><br/>
												<span className="text-[12px] text-blue-700">המסוף יישלח אליך תוך 3-10 ימי עסקים לכתובת שציינת</span>
											</div>
										</div>

										<div className="flex items-start gap-2.5">
											<div className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</div>
											<div className="text-[13px] text-blue-900">
												<strong>קבלת פרטי הפעלה</strong><br/>
												<span className="text-[12px] text-blue-700">תקבל מייל עם מספר מסוף, שם משתמש וסיסמה להפעלה</span>
											</div>
										</div>

										<div className="flex items-start gap-2.5">
											<div className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">4</div>
											<div className="text-[13px] text-blue-900">
												<strong>הפעלת המסוף במערכת</strong><br/>
												<span className="text-[12px] text-blue-700">היכנס ל"סליקה וחשבוניות" ולחץ על "הוסף מסוף" להפעיל את המסוף</span>
											</div>
										</div>
									</div>
								</div>

								<div className="bg-amber-50 rounded-xl p-3 border border-amber-200 text-right">
									<p className="text-amber-900 text-[12px]">
										<strong>שים לב:</strong> שמור את המייל עם פרטי ההפעלה! תצטרך אותם כדי לחבר את המסוף למערכת.
									</p>
								</div>
							</div>
							<button
								className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold text-[16px] hover:from-blue-700 hover:to-blue-800 active:scale-[0.98] transition-all shadow-lg"
								onClick={handleClose}
							>
								הבנתי, תודה!
							</button>
						</div>
					)}
			</BottomSheet>

			<BottomSheet
				isOpen={showMonthlyDialog && !showStatusSummary}
				onClose={handleClose}
				ariaLabel="תשלום חודשי"
			>
				<div className="bg-white border-b border-gray-100 p-4">
					<h2 className="text-[20px] font-semibold text-gray-900 text-center">תשלום חודשי</h2>
				</div>

				<div className="p-6 space-y-4">
							<div className="bg-gray-50 rounded-2xl p-4">
								<div className="space-y-2 mb-3">
									<div className="flex items-center justify-between">
										<span className="text-[14px] text-gray-600">מערכת ניהול + סליקה</span>
										<span className="text-[14px] font-medium text-gray-900">$20</span>
									</div>
									<div className="flex items-center justify-between">
										<span className="text-[14px] text-gray-600">הפקת חשבוניות</span>
										<span className="text-[14px] font-medium text-gray-900">$8</span>
									</div>
									<div className="border-t border-gray-200 my-2"></div>
									<div className="flex items-center justify-between">
										<span className="text-[15px] font-semibold text-gray-900">סה"כ לחודש</span>
										<span className="text-[20px] font-bold text-blue-600">$28</span>
									</div>
								</div>
							</div>

							<p className="text-[14px] text-gray-600 text-center">
								להמשך השירות, יש להשלים את התשלום החודשי
							</p>
				</div>

				<div className="bg-white border-t border-gray-100 p-4">
					<button
						className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors active:scale-[0.98] text-[16px]"
						onClick={handleMonthlyPayment}
					>
						המשך לתשלום
					</button>
				</div>
			</BottomSheet>

			{/* מסך טעינה מלא */}
			{loadingMessage && (
				<FullScreenLoader message={loadingMessage} submessage={loadingSubMessage} />
			)}

			{/* סיכום סטטוס - עיצוב iOS */}
			{showStatusSummary && statusSummaryData && (
				<>
					{/* רקע כהה */}
					<div
						className="fixed inset-0 z-[10000] bg-black/40 backdrop-blur-sm animate-fadeIn"
						onClick={() => {
							setShowStatusSummary(false);
							if (onContinueComplete) onContinueComplete();
						}}
					/>

					{/* תוכן הפופאפ */}
					<div className="fixed inset-x-0 bottom-0 z-[10001] animate-slideUp" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
						<div className="bg-white rounded-t-[28px] shadow-2xl max-w-2xl mx-auto overflow-hidden">
							{/* ידית גרירה */}
							<div className="flex justify-center pt-3 pb-2">
								<div className="w-10 h-1 bg-gray-300 rounded-full" />
							</div>

							{/* כותרת */}
							<div className="px-6 pt-2 pb-4">
								<h2 className="text-[22px] font-bold text-gray-900 text-center mb-1">המשך הזמנת מסוף</h2>
								<p className="text-[15px] text-gray-500 text-center">בוא נשלים את ההזמנה שלך</p>
							</div>

							{/* תוכן */}
							<div className="px-6 pb-6 space-y-4 max-h-[70vh] overflow-y-auto">
								{/* דגם המסוף */}
								<div className="bg-gray-50 rounded-2xl p-4">
									<div className="flex items-center justify-between">
										<span className="text-[15px] font-medium text-gray-700">דגם המסוף</span>
										<span className="text-[17px] font-bold text-blue-600">{statusSummaryData.terminalModel || 'לא נבחר'}</span>
									</div>
								</div>

								{/* סטטוס תשלומים */}
								<div className="space-y-3">
									<div className={`flex items-center gap-3 p-4 rounded-2xl ${
										statusSummaryData.oneTimeCompleted
											? 'bg-green-50'
											: 'bg-amber-50'
									}`}>
										<div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
											statusSummaryData.oneTimeCompleted
												? 'bg-green-500'
												: 'bg-amber-500'
										}`}>
											{statusSummaryData.oneTimeCompleted ? (
												<Check className="h-5 w-5 text-white" strokeWidth={3} />
											) : (
												<X className="h-5 w-5 text-white" strokeWidth={3} />
											)}
										</div>
										<div className="flex-1">
											<p className="font-semibold text-[16px] text-gray-900">תשלום חד פעמי</p>
											<p className="text-[14px] text-gray-600">
												{statusSummaryData.oneTimeCompleted ? 'הושלם בהצלחה' : 'טרם בוצע'}
											</p>
										</div>
									</div>

									<div className={`flex items-center gap-3 p-4 rounded-2xl ${
										statusSummaryData.monthlyCompleted
											? 'bg-green-50'
											: 'bg-amber-50'
									}`}>
										<div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
											statusSummaryData.monthlyCompleted
												? 'bg-green-500'
												: 'bg-amber-500'
										}`}>
											{statusSummaryData.monthlyCompleted ? (
												<Check className="h-5 w-5 text-white" strokeWidth={3} />
											) : (
												<X className="h-5 w-5 text-white" strokeWidth={3} />
											)}
										</div>
										<div className="flex-1">
											<p className="font-semibold text-[16px] text-gray-900">תשלום חודשי</p>
											<p className="text-[14px] text-gray-600">
												{statusSummaryData.monthlyCompleted ? 'הושלם בהצלחה' : 'טרם בוצע'}
											</p>
										</div>
									</div>
								</div>

								{/* השלב הבא */}
								<div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
									<div className="flex items-start gap-2">
										<Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
										<div className="flex-1">
											<h4 className="font-bold text-[16px] text-blue-900 mb-1">השלב הבא:</h4>
											<p className="text-[15px] text-blue-800 leading-relaxed">
												{!statusSummaryData.oneTimeCompleted && 'אתה תועבר עכשיו לתשלום חד פעמי למסוף ($445)'}
												{statusSummaryData.oneTimeCompleted && !statusSummaryData.monthlyCompleted && 'אתה תועבר עכשיו לתשלום חודשי לשירותים ($28/חודש)'}
												{statusSummaryData.oneTimeCompleted && statusSummaryData.monthlyCompleted && 'אתה תועבר עכשיו למילוי טופס פרטי המסוף'}
											</p>
										</div>
									</div>
								</div>
							</div>

							{/* כפתורים */}
							<div className="px-6 pb-6 pt-4 bg-white border-t border-gray-100 space-y-3">
								<button
									onClick={handleContinueAfterSummary}
									className="w-full py-4 bg-blue-600 text-white rounded-2xl font-bold text-[17px] hover:bg-blue-700 active:scale-[0.98] transition-all shadow-sm"
								>
									המשך
								</button>
								<button
									onClick={() => {
										setShowStatusSummary(false);
										if (onContinueComplete) onContinueComplete();
									}}
									className="w-full py-3.5 bg-gray-100 text-gray-700 rounded-2xl font-semibold text-[16px] hover:bg-gray-200 active:scale-[0.98] transition-all"
								>
									ביטול
								</button>
							</div>
						</div>
					</div>

					<style>{`
						@keyframes fadeIn {
							from { opacity: 0; }
							to { opacity: 1; }
						}
						@keyframes slideUp {
							from {
								transform: translateY(100%);
								opacity: 0;
							}
							to {
								transform: translateY(0);
								opacity: 1;
							}
						}
						.animate-fadeIn {
							animation: fadeIn 0.2s ease-out;
						}
						.animate-slideUp {
							animation: slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1);
						}
					`}</style>
				</>
			)}
		</>
	);
}

// Helper: get first brand for each provider in the selected brands
function getFirstBrandForProviders(brands: string[], providers: Record<string, string>) {
	const first: Record<string, string> = {};
	for (const brand of brands) {
		const provider = providers[brand];
		if (provider && !first[provider]) {
			first[provider] = brand;
		}
	}
	return first;
}

